import { useEffect, useState } from "react";
import { api, fmtDate, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, DownloadSimple, DotsThreeVertical, Eye, PencilSimple } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { toast } from "sonner";
import ActionsMenu from "@/components/ActionsMenu";
import SearchableSelect from "@/components/SearchableSelect";

async function downloadFile(url, filename) {
  const tok = localStorage.getItem("smipl_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

const EMPTY = {
  name: "", phone: "", phone_country_code: "+91", email: "",
  company_name: "", gstin: "", pan: "",
  billing: { address1: "", address2: "", pincode: "", city: "", state: "", country: "India" },
  opening_balance: 0, opening_balance_type: "debit",
  tds_applicable: false, tcs_applicable: false, rcm_applicable: false,
  price_list: "", debit_limit: 0, tags: [], notes: "", cc_emails: [], default_due_date: "",
  group: "", create_customer_as_well: false, linked_customer_id: "",
};

const STATE_OPTS = [
  "01-Jammu and Kashmir","02-Himachal Pradesh","03-Punjab","04-Chandigarh","05-Uttarakhand","06-Haryana","07-Delhi",
  "08-Rajasthan","09-Uttar Pradesh","10-Bihar","11-Sikkim","12-Arunachal Pradesh","13-Nagaland","14-Manipur",
  "15-Mizoram","16-Tripura","17-Meghalaya","18-Assam","19-West Bengal","20-Jharkhand","21-Odisha","22-Chhattisgarh",
  "23-Madhya Pradesh","24-Gujarat","27-Maharashtra","29-Karnataka","30-Goa","32-Kerala","33-Tamil Nadu",
  "34-Puducherry","36-Telangana","37-Andhra Pradesh","38-Ladakh",
].map((v) => ({ value: v, label: v }));

export default function Vendors() {
  const { user } = useAuth();
  const canDelete = user?.role === "super_admin";
  const canManage = ["super_admin", "hr"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const load = () => api.get("/vendors", { params: { q: q || undefined } }).then((r) => { setItems(r.data); setSelected(new Set()); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);
  useEffect(() => { api.get("/accounts").then((r) => setCustomers(r.data)); }, []);

  const openForm = (v) => {
    setEditing(v || null);
    setForm(v
      ? { ...EMPTY, ...v, billing: { ...EMPTY.billing, ...(v.billing || {}) } }
      : EMPTY);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.put(`/vendors/${editing.id}`, form);
      else await api.post("/vendors", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeOne = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleSel = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  return (
    <div data-testid="vendors-page">
      <PageHeader
        title="Vendors"
        description="Vendor master — billing, tax flags and payment terms."
        action={
          <div className="flex items-center gap-2">
            <ActionsMenu
              entityLabel="Vendors"
              selectedCount={selected.size}
              canDestructive={canDelete}
              canManage={canManage}
              onImport={() => setShowImport(true)}
              onDownloadExcel={() => downloadFile(`${API_BASE}/vendors/export/excel`, "vendors.xlsx")}
              onDownloadPdf={() => downloadFile(`${API_BASE}/vendors/export/pdf`, "vendors.pdf")}
              onDownloadCsv={() => downloadFile(`${API_BASE}/vendors/export/csv`, "vendors.csv")}
              onBulkEdit={() => setShowBulkEdit(true)}
              onBulkDelete={async () => {
                try { const r = await api.post("/vendors/bulk-delete", { ids: Array.from(selected) });
                  toast.success(`Deleted ${r.data.deleted}`); load(); }
                catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
              onDeleteAll={async () => {
                try { const r = await api.post("/vendors/delete-all");
                  toast.success(`Deleted all ${r.data.deleted}`); load(); }
                catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
            />
            <Button onClick={() => openForm(null)} className="btn-brand" data-testid="vendor-new">
              <Plus size={16} className="mr-1.5" />Add Vendor
            </Button>
          </div>
        }
      />
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white dark:bg-slate-800 border rounded-md px-3 py-1.5">
          <MagnifyingGlass size={16} className="opacity-40" />
          <input data-testid="vendors-search" placeholder="Search name, company, GSTIN…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="bg-transparent outline-none text-sm w-full" />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {canDelete && <TableHead className="w-10">
                <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                       checked={selected.size === items.length && items.length > 0}
                       onChange={() => selected.size === items.length ? setSelected(new Set()) : setSelected(new Set(items.map((v) => v.id)))}
                       data-testid="vendors-select-all" />
              </TableHead>}
              <TableHead>Name / Company</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((v) => {
              const bill = v.billing || {};
              return (
                <TableRow key={v.id} className="cursor-pointer" onClick={(e) => { if (e.target.closest("[data-stop]")) return; openForm(v); }}>
                  {canDelete && <TableCell onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]" checked={selected.has(v.id)} onChange={() => toggleSel(v.id)} data-testid={`vendor-check-${v.id}`} />
                  </TableCell>}
                  <TableCell>
                    <div className="font-medium">{v.name}</div>
                    {v.company_name && v.company_name !== v.name && <div className="text-xs opacity-60">{v.company_name}</div>}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{v.gstin || "—"}</TableCell>
                  <TableCell className="text-sm">{[bill.city, bill.state, bill.country].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{v.phone || "—"}</TableCell>
                  <TableCell className="text-xs opacity-60">{fmtDate(v.created_at)}</TableCell>
                  <TableCell className="text-right" data-stop onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => openForm(v)} data-testid={`vendor-view-${v.id}`}><Eye size={14} className="mr-1" />Ledger</Button>
                      <Button size="sm" variant="outline" onClick={() => openForm(v)} data-testid={`vendor-edit-${v.id}`}><PencilSimple size={14} className="mr-1" />Edit</Button>
                      {canDelete && (
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => removeOne(v)} data-testid={`vendor-del-${v.id}`}><DotsThreeVertical size={14} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={canDelete ? 7 : 6} className="text-center text-sm opacity-60 py-10">No vendors yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <VendorFormDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm}
                        editing={editing} save={save} customers={customers} />
      <ImportVendorsDialog open={showImport} onOpenChange={setShowImport} onDone={load} />
      <BulkEditVendorsDialog open={showBulkEdit} onOpenChange={setShowBulkEdit} ids={Array.from(selected)} onDone={load} />
    </div>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testId}
      className={`relative inline-flex items-center h-6 w-12 rounded-full transition-colors ${value ? "bg-[#fd7e14]" : "bg-slate-300 dark:bg-slate-700"}`}>
      <span className={`absolute left-0.5 h-5 w-5 bg-white rounded-full shadow transform transition-transform ${value ? "translate-x-6" : ""}`} />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white pointer-events-none">{value ? "Yes" : "No"}</span>
    </button>
  );
}

function VendorFormDialog({ open, onOpenChange, form, setForm, editing, save, customers }) {
  const [more, setMore] = useState(false);
  const b = form.billing || {};
  const setB = (k, v) => setForm({ ...form, billing: { ...b, [k]: v } });

  const fetchGST = async () => {
    const g = (form.gstin || "").trim();
    if (!g) return toast.error("Enter GSTIN first");
    try {
      const r = await api.get(`/gstin/lookup/${g}`);
      const next = { ...form };
      if (r.data.legal_name && !next.company_name) next.company_name = r.data.legal_name;
      if (r.data.pan && !next.pan) next.pan = r.data.pan;
      const bb = { ...(next.billing || {}) };
      if (r.data.address && !bb.address1) bb.address1 = r.data.address;
      if (r.data.state && !bb.state) bb.state = `${r.data.state_code}-${r.data.state.toUpperCase()}`;
      if (r.data.pincode && !bb.pincode) bb.pincode = r.data.pincode;
      next.billing = bb;
      setForm(next);
      toast.success(r.data.online ? "Fetched from GST portal" : `GSTIN valid · State: ${r.data.state}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid GSTIN"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Vendor</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Basic Details */}
          <section>
            <h4 className="text-sm font-semibold mb-2 flex items-center justify-between">
              <span>Basic Details</span>
              <label className="text-xs font-normal flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" className="h-3.5 w-3.5 accent-[#fd7e14]"
                       checked={!!form.linked_customer_id}
                       onChange={() => setForm({ ...form, linked_customer_id: form.linked_customer_id ? "" : (customers[0]?.id || "") })} />
                Link Customer
              </label>
            </h4>
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-3">
              <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ratan TATA" data-testid="vendor-name" /></Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Phone">
                  <div className="flex gap-2">
                    <SearchableSelect value={form.phone_country_code} onChange={(v) => setForm({ ...form, phone_country_code: v })}
                      options={[{ value: "+91", label: "+91 India" }, { value: "+1", label: "+1 US" }, { value: "+44", label: "+44 UK" }]} className="w-32" />
                    <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="8121335436" />
                  </div>
                </Field>
                <Field label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              </div>
              {form.linked_customer_id !== undefined && form.linked_customer_id !== "" && (
                <Field label="Linked Customer">
                  <SearchableSelect value={form.linked_customer_id} onChange={(v) => setForm({ ...form, linked_customer_id: v })}
                    options={customers.map((c) => ({ value: c.id, label: c.company_name }))} clearable />
                </Field>
              )}
            </div>
          </section>

          {/* Company Details */}
          <section>
            <h4 className="text-sm font-semibold mb-2">Company Details</h4>
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Company"><Input value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="NEXTSPEED TECHNOLOGIES PRIVATE LIMITED" /></Field>
                <Field label="GSTIN">
                  <div className="flex gap-2">
                    <Input value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="29AABCT1332L000" data-testid="vendor-gstin" />
                    <Button type="button" variant="outline" size="sm" onClick={fetchGST} data-testid="vendor-gstin-fetch">Fetch Details</Button>
                  </div>
                </Field>
              </div>
              <div>
                <h5 className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-2">Billing Address</h5>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Address Line 1"><Input value={b.address1 || ""} onChange={(e) => setB("address1", e.target.value)} /></Field>
                  <Field label="Address Line 2"><Input value={b.address2 || ""} onChange={(e) => setB("address2", e.target.value)} /></Field>
                  <Field label="Pincode"><Input value={b.pincode || ""} onChange={(e) => setB("pincode", e.target.value)} /></Field>
                  <Field label="State"><SearchableSelect value={b.state || ""} onChange={(v) => setB("state", v)} options={STATE_OPTS} placeholder="Select State" clearable /></Field>
                  <Field label="City"><Input value={b.city || ""} onChange={(e) => setB("city", e.target.value)} /></Field>
                  <Field label="Country"><Input value={b.country || "India"} onChange={(e) => setB("country", e.target.value)} /></Field>
                </div>
              </div>
            </div>
          </section>

          {/* Optional Details */}
          <section>
            <h4 className="text-sm font-semibold mb-2">Optional Details</h4>
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-2">Opening Balance</div>
                <div className="flex gap-4 mb-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.opening_balance_type === "debit"} onChange={() => setForm({ ...form, opening_balance_type: "debit" })} />
                    <span className="text-sm">Debit</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={form.opening_balance_type === "credit"} onChange={() => setForm({ ...form, opening_balance_type: "credit" })} />
                    <span className="text-sm">Credit</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" value={form.opening_balance || 0} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} placeholder="Enter amount" className="flex-1" />
                  <span className="text-xs text-red-600 font-medium whitespace-nowrap">{form.opening_balance_type === "debit" ? "Vendor Pays You ₹" : "You Pay Vendor ₹"}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><div className="text-xs mb-1">TDS</div><Toggle value={form.tds_applicable} onChange={(v) => setForm({ ...form, tds_applicable: v })} testId="vendor-tds" /></div>
                <div><div className="text-xs mb-1">TCS</div><Toggle value={form.tcs_applicable} onChange={(v) => setForm({ ...form, tcs_applicable: v })} testId="vendor-tcs" /></div>
                <div><div className="text-xs mb-1">RCM Applicable</div><Toggle value={form.rcm_applicable} onChange={(v) => setForm({ ...form, rcm_applicable: v })} testId="vendor-rcm" /></div>
              </div>
              <p className="text-xs opacity-60">If RCM enabled, it will be turned on automatically when creating a document.</p>
            </div>
          </section>

          {/* Customer Linking (bulk) */}
          {!editing && (
            <section>
              <h4 className="text-sm font-semibold mb-2">Customer Linking</h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                         checked={!!form.create_customer_as_well}
                         onChange={(e) => setForm({ ...form, create_customer_as_well: e.target.checked })}
                         data-testid="vendor-create-customer" />
                  Create customer with same details
                </label>
              </div>
            </section>
          )}

          {/* More Details */}
          <section>
            <button type="button" onClick={() => setMore((m) => !m)}
              className="w-full text-left bg-orange-50/60 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/40 rounded-md p-3">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <span>{more ? "▾" : "▸"}</span>
                <span>More Details?</span>
              </div>
              <div className="text-xs opacity-70 mt-0.5">Add CC Emails, Tags, Notes, Price List, Debit Limit</div>
            </button>
            {more && (
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-3 mt-2">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="PAN No."><Input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABJCS5210K" /></Field>
                  <Field label="Debit Limit"><Input type="number" value={form.debit_limit || 0} onChange={(e) => setForm({ ...form, debit_limit: Number(e.target.value) })} /></Field>
                  <Field label="Price List"><Input value={form.price_list || ""} onChange={(e) => setForm({ ...form, price_list: e.target.value })} placeholder="Select Price List" /></Field>
                  <Field label="Default Due Date"><Input value={form.default_due_date || ""} onChange={(e) => setForm({ ...form, default_due_date: e.target.value })} placeholder="e.g. Net 30" /></Field>
                  <Field label="Tags (comma-separated)"><Input value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="CC Emails (comma-separated)"><Input value={(form.cc_emails || []).join(", ")} onChange={(e) => setForm({ ...form, cc_emails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
                </div>
                <Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              </div>
            )}
          </section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} className="btn-brand" data-testid="vendor-save">{editing ? "Save" : "Add Vendor"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportVendorsDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Pick an Excel file first");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/vendors/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} vendor(s)${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      onOpenChange(false); setFile(null); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Import Vendors from Excel</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div><div className="font-medium mb-1">Step 1 — Download template</div>
            <Button size="sm" variant="outline" onClick={() => downloadFile(`${API_BASE}/vendors/import/template`, "vendors_import_template.xlsx")}><DownloadSimple size={14} className="mr-1.5" />Download</Button>
          </div>
          <div><div className="font-medium mb-1">Step 2 — Upload your filled file</div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!file || busy} className="btn-brand">{busy ? "Importing…" : "Import"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkEditVendorsDialog({ open, onOpenChange, ids, onDone }) {
  const [upd, setUpd] = useState({ group: "", notes: "" });
  const submit = async () => {
    const payload = Object.fromEntries(Object.entries(upd).filter(([, v]) => v));
    if (!Object.keys(payload).length) return toast.error("Fill at least one field");
    try { const r = await api.post("/vendors/bulk-update", { ids, updates: payload });
      toast.success(`Updated ${r.data.updated}`); onOpenChange(false); setUpd({ group: "", notes: "" }); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Bulk edit {ids.length} vendor(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <Field label="Group"><Input value={upd.group} onChange={(e) => setUpd({ ...upd, group: e.target.value })} /></Field>
          <Field label="Notes"><Input value={upd.notes} onChange={(e) => setUpd({ ...upd, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
